/*
 * Fridge naming, the same as in back-office-board (src/utils/install-label.js and the grouping of
 * components/atoms/a-select-install). Keep both in sync: a fridge must be called the same in both apps.
 */

const SEPARATOR = ' · ';

export interface LabelMerchant {
  _id?: string;
  name?: string;
  siteCode?: string | null;
}

export interface LabelInstall {
  name?: string;
  inventoryNumber?: number;
}

export interface LabelMerchantGroup {
  name?: string;
  merchantIds?: unknown[];
}

const clean = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const isSameName = (a: string, b: string): boolean => !!a && a.toLowerCase() === b.toLowerCase();

const compareText = (a: string, b: string): number => a.localeCompare(b, undefined, { sensitivity: 'base' });

/** Label of a merchant for dropdowns and lists: siteCode · name */
export function getMerchantLabel(merchant: LabelMerchant | undefined | null): string {
  if (!merchant) return '';
  return [clean(merchant.siteCode), clean(merchant.name)].filter(Boolean).join(SEPARATOR);
}

/** Short label of a merchant: the siteCode, the name when there is none */
export function getMerchantTag(merchant: LabelMerchant | undefined | null): string {
  if (!merchant) return '';
  return clean(merchant.siteCode) || clean(merchant.name);
}

/**
 * Label of a fridge:
 * external user: siteCode · merchant name · #inventoryNumber · displayName
 * internal user: #inventoryNumber · siteCode · merchant name · displayName
 * `name` is the display name, it's skipped when it repeats the merchant name.
 * `withMerchant: false` leaves out siteCode and merchant name, e.g. when the merchant is already shown around.
 */
export function getInstallLabel(
  install: LabelInstall | undefined | null,
  merchant: LabelMerchant | undefined | null,
  { isInternal = false, withMerchant = true } = {},
): string {
  if (!install) return '';

  const siteCode = withMerchant ? clean(merchant?.siteCode) : '';
  const merchantName = withMerchant ? clean(merchant?.name) : '';
  const displayName = clean(install.name);
  const inventoryNumber = install.inventoryNumber ? `#${install.inventoryNumber}` : '';

  const parts = isInternal ? [inventoryNumber, siteCode, merchantName] : [siteCode, merchantName, inventoryNumber];

  if (!isSameName(merchantName, displayName)) parts.push(displayName);

  return parts.filter(Boolean).join(SEPARATOR);
}

export interface InstallLabelParts {
  /** '#79' */
  number: string;
  /** 'siteCode · merchant name' */
  merchant: string;
  /** display name, '' when it repeats the merchant name */
  name: string;
}

/** Parts of the fridge label for places where they are styled separately, e.g. a fridge card or a page header. */
export function getInstallLabelParts(
  install: LabelInstall | undefined | null,
  merchant: LabelMerchant | undefined | null,
  { withMerchant = true } = {},
): InstallLabelParts {
  if (!install) return { number: '', merchant: '', name: '' };

  // the name repeats the merchant whether the merchant is shown or not — the same as in back-office-board
  const merchantName = withMerchant ? clean(merchant?.name) : '';
  const displayName = clean(install.name);

  return {
    number: install.inventoryNumber ? `#${install.inventoryNumber}` : '',
    merchant: withMerchant ? getMerchantLabel(merchant) : '',
    name: isSameName(merchantName, displayName) ? '' : displayName,
  };
}

/** A search query that is a number (optionally with '#') is first of all an inventory number. */
export function getQueryNumber(query: string): string {
  const q = (query || '').trim().replace(/^#/, '');
  return /^\d+$/.test(q) ? q : '';
}

/** The exact inventory number first, then the ones starting with it, then containing it; stable otherwise. */
export function sortByInventoryNumber<T extends LabelInstall>(installs: T[], queryNumber: string): T[] {
  if (!queryNumber) return installs;
  const rank = (install: T): number => {
    const inventoryNumber = String(install.inventoryNumber || '');
    if (inventoryNumber === queryNumber) return 0;
    if (inventoryNumber.startsWith(queryNumber)) return 1;
    if (inventoryNumber.includes(queryNumber)) return 2;
    return 3;
  };
  return installs
    .map((install, index) => ({ install, index, rank: rank(install) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((item) => item.install);
}

export interface InstallSectionItem<T> {
  install: T;
  /** false in a merchant section: its title already has the merchant */
  showMerchant: boolean;
}

export interface InstallSection<T> {
  key: string;
  label: string;
  items: InstallSectionItem<T>[];
}

/**
 * Groups fridges by merchant group, then by merchant — the same as the fridge dropdown of back-office-board:
 * - a merchant group gets one section titled by its name, its fridges keep the merchant in the label;
 * - of the merchants without a group, one with several fridges gets a section titled "siteCode · merchant name",
 *   the ones with one fridge share the "Without group" section ("Other" when nobody has a group), which goes first.
 * Merchants without a group go last. Returns null when sections make no sense (a single one, or no merchants known).
 */
export function buildInstallSections<T extends LabelInstall & { merchantId?: unknown }>(
  installs: T[],
  merchantsById: Record<string, LabelMerchant>,
  merchantGroupByMerchantId: Record<string, LabelMerchantGroup>,
  labels: { withoutGroup: string; other: string },
): InstallSection<T>[] | null {
  if (!Object.keys(merchantsById).length) return null;

  type MerchantBucket = {
    merchantId: string;
    isKnownMerchant: boolean;
    groupName: string;
    merchantLabel: string;
    merchantName: string;
    installs: T[];
  };
  const byMerchant: Record<string, MerchantBucket> = {};
  const order: string[] = [];
  installs.forEach((install) => {
    const merchantId = String(install.merchantId ?? '');
    if (!byMerchant[merchantId]) {
      const merchant = merchantsById[merchantId];
      const group = merchantGroupByMerchantId[merchantId];
      const merchantLabel = getMerchantLabel(merchant);
      byMerchant[merchantId] = {
        merchantId,
        // the merchant of a fridge the user sees directly (allowedFridges) may be not among his merchants
        isKnownMerchant: !!merchant,
        groupName: clean(group?.name),
        merchantLabel,
        // by name, not by the label: not every merchant has a siteCode
        merchantName: (clean(merchant?.name) || merchantLabel).trim(),
        installs: [],
      };
      order.push(merchantId);
    }
    byMerchant[merchantId].installs.push(install);
  });

  const merchants = order
    .map((id) => byMerchant[id])
    .sort(
      (a, b) =>
        Number(!a.groupName) - Number(!b.groupName) ||
        compareText(a.groupName, b.groupName) ||
        compareText(a.merchantName, b.merchantName),
    );

  const ungroupedLabel = merchants.some(({ groupName }) => groupName) ? labels.withoutGroup : labels.other;
  const byNumber = (a: T, b: T): number => (a.inventoryNumber || 0) - (b.inventoryNumber || 0);

  const groups: { name: string; shared: InstallSection<T>; merchantSections: InstallSection<T>[] }[] = [];
  merchants.forEach(({ merchantId, isKnownMerchant, groupName, merchantLabel, installs: merchantInstalls }) => {
    let group = groups[groups.length - 1];
    if (!group || group.name !== groupName) {
      group = {
        name: groupName,
        shared: { key: `group:${groupName}`, label: groupName || ungroupedLabel, items: [] },
        merchantSections: [],
      };
      groups.push(group);
    }
    // in a group the merchant is in the label, it gets no section of its own
    if (groupName || merchantInstalls.length === 1 || !isKnownMerchant) {
      group.shared.items.push(...[...merchantInstalls].sort(byNumber).map((install) => ({ install, showMerchant: true })));
      return;
    }
    group.merchantSections.push({
      key: `merchant:${merchantId}`,
      label: merchantLabel,
      items: [...merchantInstalls].sort(byNumber).map((install) => ({ install, showMerchant: false })),
    });
  });

  const sections = groups.flatMap(({ shared, merchantSections }) =>
    shared.items.length ? [shared, ...merchantSections] : merchantSections,
  );
  // a single title over the whole list (e.g. a user of one merchant) says nothing
  return sections.length > 1 ? sections : null;
}
